import { useEffect, useState } from 'react';
import BookingLandingPage from './components/BookingLandingPage';
import BookingFormPage from './components/BookingFormPage'; // to be built for date/time
import { dummyBookingSetting } from 'constants/dummy';
import axios from 'axios';
import { BOOKING_ACCESS_WEBHOOKS_URL, GET_BOOKING_SETTING_WEBHOOKS_URL, GET_TENANT_WEBHOOK_URL } from 'constants/url';
import { Backdrop, CircularProgress } from '@material-ui/core';
import NotFoundPage from 'pages/NotFoundPage';

const INVALID_LINK_MESSAGE =
  'This booking link is no longer valid. Please enter your quotation number and PIN, or contact your service provider to get your PIN.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again in a few minutes.';

/**
 * Exchange a WhatsApp link token or a quotation number + PIN for the accessToken sent with every booking request.
 * Resolves to { accessToken } or { error } with a message for the customer; never throws.
 */
const requestBookingAccess = async (tenant: string, body: object): Promise<{ accessToken?: string; error?: string }> => {
  try {
    const response = await axios.post(BOOKING_ACCESS_WEBHOOKS_URL(tenant), body);
    // The global axios interceptor resolves network errors with no response
    if (!response?.data?.accessToken) {
      return { error: GENERIC_ERROR_MESSAGE };
    }
    return { accessToken: response.data.accessToken };
  } catch (err) {
    // The global axios interceptor rejects with the response itself
    const error = err as any;
    return { error: error?.data?.message || GENERIC_ERROR_MESSAGE };
  }
};

const BookingPage = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [initialQuotation, setInitialQuotation] = useState<string>('');
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [bookingSetting, setBookingSetting] = useState<any>(dummyBookingSetting);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [tenant, setTenant] = useState<string | null>(null);
  const [isTenantInvalid, setIsTenantInvalid] = useState(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const params = new URLSearchParams(window.location.search);
    const linkToken = params.get('t');
    // quotation_id: links sent before booking PINs existed; quotationCode: older customer booking link format
    const quotation = (params.get('quotation_id') || params.get('quotationCode'))?.replace(/[^\d]/g, '') || '';

    const resolveTenantAndLink = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(GET_TENANT_WEBHOOK_URL(hostname));

        if (!res?.data) {
          setIsTenantInvalid(true); // ⚠️ Domain not registered
          return;
        }

        const resolvedTenant = String(res.data);
        setTenant(resolvedTenant);
        setInitialQuotation(quotation);

        if (linkToken) {
          const { accessToken: token } = await requestBookingAccess(resolvedTenant, { token: linkToken });
          if (token) {
            setAccessToken(token);
          } else {
            setLinkNotice(INVALID_LINK_MESSAGE);
          }
        }
      } catch (error) {
        console.error('❌ Failed to resolve tenant:', error);
        setIsTenantInvalid(true); // ⚠️ Treat all errors as "not found"
      } finally {
        setIsLoading(false);
      }
    };

    resolveTenantAndLink();
  }, []);

  useEffect(() => {
    if (!tenant) return;

    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(GET_BOOKING_SETTING_WEBHOOKS_URL(tenant!));
        const data = res.data;

        const updatedSettings = {
          ...data,
          TimeSlots: data.TimeSlots?.split(',') ?? []
        };

        setBookingSetting(updatedSettings);
      } catch (error) {
        console.error('❌ Failed to load settings:', error);
        alert('Failed to load');
        setBookingSetting(dummyBookingSetting);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();

    return () => {};
  }, [tenant]);

  const handleSubmitPin = async (quotation: string, pin: string): Promise<string | null> => {
    if (!tenant) {
      return GENERIC_ERROR_MESSAGE;
    }

    setIsLoading(true);
    try {
      const { accessToken: token, error } = await requestBookingAccess(tenant, { quotationNumber: quotation, pin });
      if (!token) {
        return error || GENERIC_ERROR_MESSAGE;
      }

      setLinkNotice(null);
      setAccessToken(token);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isTenantInvalid ? (
        <NotFoundPage hideBackButton={true} />
      ) : accessToken && tenant ? (
        <BookingFormPage accessToken={accessToken} tenant={tenant} bookingSetting={bookingSetting} onBack={() => setAccessToken(null)} />
      ) : (
        <BookingLandingPage
          bookingSetting={bookingSetting}
          initialQuotation={initialQuotation}
          notice={linkNotice}
          isSubmitting={isLoading}
          onSubmit={handleSubmitPin}
        />
      )}

      <Backdrop open={isLoading} style={{ zIndex: 1300, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </>
  );
};

export default BookingPage;
